import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Layers,
  Zap,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  RefreshCw,
  ExternalLink,
  Target,
  ArrowRight,
  Image as ImageIcon,
  Sliders,
  DollarSign,
  Send,
  Eye,
  MessageSquare,
  Globe,
  Share2,
  FileText,
  UserCheck,
  Search,
  BarChart2
} from 'lucide-react';
import {
  campaignsApi,
  type MCAGenerateResult,
  type CampaignCreative,
  type CampaignAudience,
  type KeywordResearchResult,
  type ProductCatalogItem
} from '../campaignsApi';
import { KeywordResearchPanel } from './KeywordResearchPanel';
import { useSessionStore } from '../../../stores/sessionStore';

interface MCACampaignStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (campaignId: string) => void;
}

const CONSCIOUSNESS_BADGES: Record<string, { label: string; bg: string; text: string; border: string }> = {
  unaware: { label: '1. Inconsciente (Dor Latente)', bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20' },
  problem_aware: { label: '2. Consciente do Problema (Sintomas)', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  solution_aware: { label: '3. Consciente da Solução (Novo Método)', bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
  product_aware: { label: '4. Consciente do Produto (Autoridade)', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  most_aware: { label: '5. Oferta Irrecusável (Fundo de Funil)', bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' }
};

export const MCACampaignStudioModal: React.FC<MCACampaignStudioModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const activeTenantLogo = useSessionStore((state) => state.activeTenantLogo);
  const user = useSessionStore((state) => state.user);
  const [step, setStep] = useState<number>(1); // 1: Produto & Budget, 2: Validar Pesquisa, 3: CGO Strategy, 4: Matriz 5 Ângulos

  // Catálogo de Produtos
  const [products, setProducts] = useState<ProductCatalogItem[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedProductIntel, setSelectedProductIntel] = useState<any>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [destinationType, setDestinationType] = useState<'whatsapp' | 'lead_form' | 'landing_page' | 'checkout'>('whatsapp');
  const [landingUrl, setLandingUrl] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [budgetInitial, setBudgetInitial] = useState(50);
  const [tone, setTone] = useState('persuasivo_autoridade');
  const [metaAdAccountId, setMetaAdAccountId] = useState('');
  const [metaAccounts, setMetaAccounts] = useState<Array<{ id: string; adAccountId: string; name: string; pageName: string | null; isDefault: boolean }>>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  // Keyword Research & Validation State (Step 2)
  const [isResearching, setIsResearching] = useState(false);
  const [researchResult, setResearchResult] = useState<KeywordResearchResult | null>(null);
  const [approvedKeywords, setApprovedKeywords] = useState<string[]>([]);
  const [approvedPains, setApprovedPains] = useState<string[]>([]);

  // Generation & Result State (Step 3 e 4)
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [mcaResult, setMcaResult] = useState<MCAGenerateResult | null>(null);
  const [selectedCreativeIndex, setSelectedCreativeIndex] = useState<number>(0);
  const [synthesizingImageId, setSynthesizingImageId] = useState<string | null>(null);

  // Carregar contas Meta e Catálogo de Produtos ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      setIsLoadingAccounts(true);
      setIsLoadingProducts(true);

      campaignsApi.getMetaAccounts()
        .then((res) => {
          if (res?.accounts) {
            setMetaAccounts(res.accounts);
            const def = res.accounts.find(a => a.isDefault) || res.accounts[0];
            if (def && !metaAdAccountId) {
              setMetaAdAccountId(def.adAccountId);
            }
          }
        })
        .catch((err) => console.error('Erro ao carregar contas Meta:', err))
        .finally(() => setIsLoadingAccounts(false));

      campaignsApi.getProducts()
        .then((res: any) => {
          const items = Array.isArray(res) ? res : (res?.items || []);
          setProducts(items);
          if (items.length > 0 && !selectedProductId) {
            setSelectedProductId(items[0].id);
          }
        })
        .catch((err) => console.error('Erro ao carregar produtos do catálogo:', err))
        .finally(() => setIsLoadingProducts(false));
    }
  }, [isOpen]);

  // Carregar inteligência do produto selecionado
  useEffect(() => {
    if (selectedProductId) {
      campaignsApi.getProductIntelligence(selectedProductId)
        .then((intel) => {
          setSelectedProductIntel(intel);
          const p = products.find(x => x.id === selectedProductId);
          if (p && !name) {
            setName(`Campanha ${p.name} - ${new Date().toLocaleDateString('pt-BR', { month: 'short' })}`);
          }
          if (p?.landing_url && destinationType === 'landing_page') {
            setLandingUrl(p.landing_url);
          }
        })
        .catch((err) => console.warn('Erro ao carregar intel do produto:', err));
    }
  }, [selectedProductId, products]);

  if (!isOpen) return null;

  const currentProduct = products.find(p => p.id === selectedProductId);

  // Ação Step 1: Dispara a pesquisa automática de keywords e avança para Validação (Step 2)
  const handleStartResearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErrorMsg('Informe o Nome de Identificação da Campanha.');
      return;
    }
    if (!selectedProductId && !currentProduct) {
      setErrorMsg('Selecione um Produto cadastrado no catálogo.');
      return;
    }

    setIsResearching(true);
    setErrorMsg(null);

    try {
      const res = await campaignsApi.researchKeywords({
        product_id: selectedProductId,
        budget_initial: budgetInitial,
        custom_product_name: currentProduct?.name,
        custom_niche: currentProduct?.target_segment
      });

      if (res.success && res.data) {
        setResearchResult(res.data);
        const allPains = [
          ...(res.data.catalogPains || []),
          ...(res.data.expandedPains || [])
        ];
        setApprovedPains(allPains);
        setApprovedKeywords((res.data.topKeywords || []).map(k => k.keyword));
        setStep(2);
      } else {
        throw new Error('Falha ao processar pesquisa de mercado.');
      }
    } catch (err: any) {
      console.error('[MCA Studio] Research error:', err);
      setErrorMsg(err.message || 'Erro ao realizar pesquisa de palavras-chave. Verifique a conexão.');
    } finally {
      setIsResearching(false);
    }
  };

  // Ação Step 2: Operador aprovou as keywords e dores -> Dispara a geração CGO (Step 3)
  const handleApproveAndGenerateMCA = async () => {
    if (!currentProduct) return;
    setIsGenerating(true);
    setErrorMsg(null);

    let effectiveLandingUrl = landingUrl.trim();
    if (destinationType === 'whatsapp') {
      const cleanNum = whatsappNumber.replace(/\D/g, '');
      const msg = encodeURIComponent(`Olá! Tenho interesse em saber mais sobre ${currentProduct.name}.`);
      effectiveLandingUrl = cleanNum ? `https://wa.me/${cleanNum}?text=${msg}` : 'https://wa.me/';
    } else if (destinationType === 'lead_form') {
      effectiveLandingUrl = 'meta_instant_form';
    } else if (!effectiveLandingUrl && currentProduct.landing_url) {
      effectiveLandingUrl = currentProduct.landing_url;
    }

    try {
      const res = await campaignsApi.generateMCA({
        name: name.trim(),
        product_id: selectedProductId,
        product_or_offer: currentProduct.name,
        market_niche: currentProduct.target_segment || 'Geral',
        target_audience_description: selectedProductIntel?.avatar?.company_profile || currentProduct.target_segment,
        pain_points: approvedPains.join('; '),
        approved_keywords: approvedKeywords,
        approved_pains: approvedPains,
        keyword_research_data: researchResult || undefined,
        product_intelligence: selectedProductIntel || undefined,
        differentiators: selectedProductIntel?.differentiators || currentProduct.differentiators || undefined,
        objections: selectedProductIntel?.objections || undefined,
        comparison: selectedProductIntel?.comparison || undefined,
        elevator_pitch: selectedProductIntel?.elevatorPitch || undefined,
        pitch: selectedProductIntel?.pitch || undefined,
        proofs: selectedProductIntel?.proofs || undefined,
        landing_url: effectiveLandingUrl,
        budget_initial: budgetInitial,
        meta_ad_account_id: metaAdAccountId || undefined,
        tone
      });

      setMcaResult(res);
      setStep(3);
    } catch (err: any) {
      console.error('[MCA Studio] Generation error:', err);
      setErrorMsg(err.message || 'Falha ao processar estratégia MCA. Verifique a conexão.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSynthesizeImage = async (creativeId: string, index: number) => {
    if (!mcaResult) return;
    setSynthesizingImageId(creativeId);

    try {
      const creativeToSynthesize = mcaResult.creatives[index];
      const res = await campaignsApi.synthesizeCreativeImage(mcaResult.campaignId, creativeId, {
        visualMetaphor: creativeToSynthesize?.visualMetaphor
      } as any);
      if (res.assetUrl) {
        setMcaResult(prev => {
          if (!prev) return prev;
          const updatedCreatives = [...prev.creatives];
          updatedCreatives[index] = {
            ...updatedCreatives[index],
            assetUrl: res.assetUrl
          };
          return { ...prev, creatives: updatedCreatives };
        });
      }
    } catch (err: any) {
      console.error('Falha na síntese de imagem:', err);
      alert(`Erro na síntese da imagem: ${err.message || 'Erro de rede'}`);
    } finally {
      setSynthesizingImageId(null);
    }
  };

  const currentCreative = mcaResult?.creatives[selectedCreativeIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-md">
      <div className="relative w-full max-w-5xl h-[90vh] bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-500 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white tracking-tight">MCA Ads Studio</h2>
                <span className="px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  5 Níveis Eugene Schwartz
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Engenharia de Criativos de Alta Conversão com Search Intelligence
              </p>
            </div>
          </div>

          {/* Stepper Dots (4 Passos) */}
          <div className="hidden sm:flex items-center gap-2 text-xs">
            <span className={`px-2.5 py-1 rounded-md font-medium transition-all ${step === 1 ? 'bg-indigo-600 text-white' : 'text-slate-400 bg-slate-800'}`}>
              1. Produto & Budget
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
            <span className={`px-2.5 py-1 rounded-md font-medium transition-all ${step === 2 ? 'bg-indigo-600 text-white' : 'text-slate-400 bg-slate-800'}`}>
              2. Validar Pesquisa
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
            <span className={`px-2.5 py-1 rounded-md font-medium transition-all ${step === 3 ? 'bg-indigo-600 text-white' : 'text-slate-400 bg-slate-800'}`}>
              3. Diagnóstico CGO
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
            <span className={`px-2.5 py-1 rounded-md font-medium transition-all ${step === 4 ? 'bg-indigo-600 text-white' : 'text-slate-400 bg-slate-800'}`}>
              4. Matriz 5 Ângulos
            </span>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {errorMsg && (
            <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm flex items-center gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-400" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* ETAPA 1: SELEÇÃO DE PRODUTO & AUTO-BRIEFING */}
          {step === 1 && (
            <form onSubmit={handleStartResearch} className="space-y-6 max-w-3xl mx-auto">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-white">Criar Nova Campanha de Alta Conversão</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Selecione o produto cadastrado e o budget diário. As dores, nicho e palavras-chave serão pesquisados automaticamente com inteligência Google e IA.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Seleção do Produto do Catálogo */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-semibold text-slate-300 flex items-center justify-between">
                    <span>Produto ou Serviço Cadastrado <span className="text-rose-400">*</span></span>
                    {isLoadingProducts && <span className="text-[11px] text-slate-400 animate-pulse">Carregando catálogo...</span>}
                  </label>
                  <select
                    required
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-medium"
                  >
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.target_segment ? `— (${p.target_segment})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Preview Automático dos Dados do Produto */}
                {currentProduct && (
                  <div className="md:col-span-2 p-4 rounded-xl bg-slate-950/60 border border-slate-800/80 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-indigo-400 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Inteligência Carregada do Catálogo
                      </span>
                      <span className="text-[11px] text-slate-400">Nicho: <strong className="text-slate-200">{currentProduct.target_segment || 'Geral'}</strong></span>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      {currentProduct.description || currentProduct.core_problem || 'Solução integrada de alta conversão.'}
                    </p>
                    {selectedProductIntel?.pains?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {selectedProductIntel.pains.slice(0, 3).map((p: string, i: number) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-800 text-slate-300 border border-slate-700/50">
                            ✓ {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Nome de Identificação da Campanha */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-semibold text-slate-300">
                    Nome de Identificação da Campanha <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Campanha CRM Suite - Setembro"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-500"
                  />
                </div>

                {/* Budget Diário Piso (Base 1x) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 flex items-center justify-between">
                    <span>Budget Diário Base (Piso) <span className="text-rose-400">*</span></span>
                    <span className="text-[11px] text-emerald-400">Projeções em 1x, 2x e 4x</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-2.5 text-slate-500 text-sm font-semibold">R$</span>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      required
                      value={budgetInitial}
                      onChange={(e) => setBudgetInitial(Number(e.target.value))}
                      className="w-full pl-10 pr-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                    />
                  </div>
                </div>

                {/* Tom de Voz */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300">
                    Tom de Voz da Copy
                  </label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                  >
                    <option value="persuasivo_autoridade">Autoridade & Alta Conversão (Direto ao ponto)</option>
                    <option value="urgente_escassez">Urgência & Escassez (Ofertas limitadas)</option>
                    <option value="educativo_consultivo">Educativo & Consultivo (Quebra de crenças)</option>
                    <option value="descontraido_moderno">Descontraído & Moderno (Engajamento jovem)</option>
                  </select>
                </div>

                {/* Destino dos Anúncios */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-semibold text-slate-300">
                    Canal de Destino dos Anúncios <span className="text-rose-400">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div
                      onClick={() => setDestinationType('whatsapp')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        destinationType === 'whatsapp'
                          ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-300 shadow-sm shadow-emerald-500/10'
                          : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-semibold">WhatsApp</span>
                      </div>
                      <p className="text-[11px] opacity-75">Conversão direta no chat</p>
                    </div>

                    <div
                      onClick={() => setDestinationType('lead_form')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        destinationType === 'lead_form'
                          ? 'bg-blue-500/10 border-blue-500/50 text-blue-300 shadow-sm shadow-blue-500/10'
                          : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <UserCheck className="w-4 h-4 text-blue-400" />
                        <span className="text-xs font-semibold">Instant Form</span>
                      </div>
                      <p className="text-[11px] opacity-75">Cadastro nativo no Meta</p>
                    </div>

                    <div
                      onClick={() => setDestinationType('landing_page')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        destinationType === 'landing_page'
                          ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-300 shadow-sm shadow-indigo-500/10'
                          : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Globe className="w-4 h-4 text-indigo-400" />
                        <span className="text-xs font-semibold">Landing Page</span>
                      </div>
                      <p className="text-[11px] opacity-75">Página de vendas externa</p>
                    </div>

                    <div
                      onClick={() => setDestinationType('checkout')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        destinationType === 'checkout'
                          ? 'bg-purple-500/10 border-purple-500/50 text-purple-300 shadow-sm shadow-purple-500/10'
                          : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-semibold">Checkout</span>
                      </div>
                      <p className="text-[11px] opacity-75">Link de pagamento direto</p>
                    </div>
                  </div>
                </div>

                {destinationType === 'whatsapp' && (
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-xs font-semibold text-slate-300">
                      Número do WhatsApp Comercial (com DDD)
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: 5511999998888"
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-500"
                    />
                  </div>
                )}

                {(destinationType === 'landing_page' || destinationType === 'checkout') && (
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-xs font-semibold text-slate-300">
                      URL de Destino
                    </label>
                    <input
                      type="url"
                      placeholder="https://minhaempresa.com.br/oferta"
                      value={landingUrl}
                      onChange={(e) => setLandingUrl(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-500"
                    />
                  </div>
                )}

                {/* Conta de Anúncios Meta Ads */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-semibold text-slate-300 flex items-center justify-between">
                    <span>Conta de Anúncios Meta Ads</span>
                    {isLoadingAccounts && <span className="text-[10px] text-slate-400">Carregando contas...</span>}
                  </label>
                  <select
                    value={metaAdAccountId}
                    onChange={(e) => setMetaAdAccountId(e.target.value)}
                    disabled={isLoadingAccounts || metaAccounts.length === 0}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all disabled:opacity-50"
                  >
                    {metaAccounts.length === 0 ? (
                      <option value="">Nenhuma conta Meta conectada (Publicação manual)</option>
                    ) : (
                      metaAccounts.map((acc) => (
                        <option key={acc.id} value={acc.adAccountId}>
                          {acc.name} ({acc.adAccountId}) {acc.pageName ? `• Página: ${acc.pageName}` : ''} {acc.isDefault ? '⭐ Padrão' : ''}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isResearching || !selectedProductId}
                  className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-lg shadow-indigo-600/25 flex items-center gap-2 transition-all disabled:opacity-50"
                >
                  {isResearching ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Pesquisando no Google & Trends...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      <span>Pesquisar Inteligência & Mercado →</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ETAPA 2: VALIDAÇÃO INTERATIVA DAS PALAVRAS-CHAVE, DORES E PROJEÇÃO */}
          {step === 2 && researchResult && (
            <div className="space-y-6 max-w-4xl mx-auto animate-in">
              <div className="flex items-center justify-between flex-wrap gap-3 pb-2 border-b border-slate-800">
                <div>
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-indigo-400" /> Validação de Inteligência & Projeção
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Revise as dores e palavras-chave descobertas. Você pode desmarcar termos irrelevantes ou adicionar novos antes de gerar a campanha.
                  </p>
                </div>
              </div>

              <KeywordResearchPanel
                data={researchResult}
                isLight={false}
                mode="interactive"
                selectedKeywords={approvedKeywords}
                selectedPains={approvedPains}
                highlightBudget={budgetInitial}
                onToggleKeyword={(k) => {
                  setApprovedKeywords(prev =>
                    prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]
                  );
                }}
                onTogglePain={(p) => {
                  setApprovedPains(prev =>
                    prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
                  );
                }}
                onAddCustomKeyword={(k) => {
                  if (!approvedKeywords.includes(k)) {
                    setApprovedKeywords(prev => [k, ...prev]);
                  }
                }}
                onAddCustomPain={(p) => {
                  if (!approvedPains.includes(p)) {
                    setApprovedPains(prev => [p, ...prev]);
                  }
                }}
              />

              <div className="pt-4 flex items-center justify-between gap-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800 transition-all flex items-center gap-1.5"
                >
                  <ChevronLeft className="w-4 h-4" /> Voltar ao Briefing
                </button>
                <button
                  type="button"
                  onClick={handleApproveAndGenerateMCA}
                  disabled={isGenerating || approvedKeywords.length === 0}
                  className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-600/25 flex items-center gap-2 transition-all disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>CGO Estruturando 5 Ângulos MCA...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Aprovar & Gerar Matriz MCA ({approvedKeywords.length} keywords) →</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ETAPA 3: DIAGNÓSTICO E PARECER DO CGO */}
          {step === 3 && mcaResult && (
            <div className="space-y-6 max-w-4xl mx-auto animate-in">
              <div className="p-5 rounded-2xl bg-gradient-to-br from-indigo-950/40 via-purple-950/30 to-slate-900 border border-indigo-500/30 shadow-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-indigo-200">Tese Estratégica do Chief Growth Officer (CGO)</h4>
                    <p className="text-xs text-slate-400">Arquitetura de Conversão & Posicionamento</p>
                  </div>
                </div>
                <p className="text-sm text-slate-200 leading-relaxed italic bg-slate-950/40 p-3.5 rounded-xl border border-slate-800">
                  "{mcaResult.cgo_rationale}"
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-indigo-300">
                  <span className="font-semibold text-slate-400">UVP Principal:</span>
                  <span className="bg-indigo-500/10 px-2.5 py-1 rounded-md border border-indigo-500/20 font-medium">
                    {mcaResult.primary_uvp}
                  </span>
                </div>
              </div>

              {/* Segmentos de Audiência Recomendados */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-purple-400" />
                  Públicos Recomendados no Meta Ads
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mcaResult.audiences?.map((aud, i) => (
                    <div key={i} className="p-4 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 transition-all">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-bold text-white">{aud.name}</span>
                        <span className="text-[10px] uppercase font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                          Segmento #{i + 1}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400">{aud.interests || aud.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Voltar à Validação
                </button>
                <button
                  type="button"
                  onClick={() => setStep(4)}
                  className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold shadow-lg shadow-indigo-500/25 flex items-center gap-2 transition-all cursor-pointer"
                >
                  Explorar Matriz 5 Ângulos & Mockup
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ETAPA 4: MATRIZ DE 5 ÂNGULOS COM MOCKUP META ADS */}
          {step === 4 && mcaResult && (
            <div className="space-y-6 animate-in">
              {/* Seletores dos 5 Ângulos */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {mcaResult.creatives.map((c, idx) => {
                  const badge = CONSCIOUSNESS_BADGES[c.consciousnessLevel || 'unaware'] || CONSCIOUSNESS_BADGES.unaware;
                  const isSelected = selectedCreativeIndex === idx;
                  return (
                    <button
                      key={c.id || idx}
                      type="button"
                      onClick={() => setSelectedCreativeIndex(idx)}
                      className={`p-3 rounded-xl text-left border transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-indigo-950/60 border-indigo-500 ring-1 ring-indigo-500/50 shadow-lg shadow-indigo-500/10'
                          : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <span className={`text-[10px] font-bold uppercase block mb-1 ${badge.text}`}>
                        Nível {idx + 1}
                      </span>
                      <span className="text-xs font-semibold text-slate-200 block truncate">
                        {c.angleName || `Ângulo ${idx + 1}`}
                      </span>
                      <span className="text-[10px] text-slate-400 block mt-0.5 truncate">
                        {c.headline}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Detalhe do Criativo Selecionado */}
              {currentCreative && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  {/* Coluna de Conteúdo e Copy */}
                  <div className="lg:col-span-7 space-y-4">
                    <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
                      <div>
                        <span className="text-[11px] font-semibold text-indigo-400 block mb-1 uppercase tracking-wider">
                          Headline do Anúncio (Título Principal)
                        </span>
                        <h4 className="text-base font-bold text-white leading-snug">
                          {currentCreative.headline}
                        </h4>
                      </div>

                      <div>
                        <span className="text-[11px] font-semibold text-slate-400 block mb-1 uppercase tracking-wider">
                          Texto Principal (Copy / Legenda)
                        </span>
                        <p className="text-xs text-slate-300 whitespace-pre-line leading-relaxed bg-slate-950/40 p-3 rounded-lg border border-slate-800">
                          {currentCreative.primaryText}
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-semibold text-indigo-400 uppercase tracking-wider flex items-center gap-1">
                            💡 Metáfora Visual (Diretiva da Cena para a IA)
                          </span>
                          <span className="text-[10px] text-slate-400">
                            Ajuste fino antes da geração
                          </span>
                        </div>
                        <textarea
                          rows={2}
                          value={currentCreative.visualMetaphor || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setMcaResult(prev => {
                              if (!prev) return prev;
                              const updatedCreatives = [...prev.creatives];
                              updatedCreatives[selectedCreativeIndex] = {
                                ...updatedCreatives[selectedCreativeIndex],
                                visualMetaphor: val
                              };
                              return { ...prev, creatives: updatedCreatives };
                            });
                          }}
                          onBlur={async () => {
                            if (mcaResult && currentCreative.id) {
                              await campaignsApi.updateCreative(mcaResult.campaignId, currentCreative.id, {
                                visualMetaphor: currentCreative.visualMetaphor
                              }).catch((err) => console.warn('Falha ao sincronizar metáfora:', err));
                            }
                          }}
                          placeholder="Ex: Profissional médico analisando prontuário em tablet luminoso em consultório humanizado moderno..."
                          className="w-full text-xs text-slate-200 bg-slate-950/60 p-2.5 rounded-lg border border-indigo-500/30 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all leading-relaxed"
                        />
                      </div>

                      <div className="pt-1">
                        <span className="text-[10px] font-semibold text-slate-400 block">Chamada para Ação (CTA)</span>
                        <span className="text-xs font-bold text-slate-200 uppercase bg-slate-800 px-2 py-1 rounded inline-block mt-0.5">
                          {currentCreative.callToActionType?.replace('_', ' ') || 'Saiba Mais'}
                        </span>
                      </div>
                    </div>

                    {/* Botão de Síntese de Imagem */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/40 border border-slate-800">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-indigo-400" />
                        <span className="text-xs text-slate-300">
                          {currentCreative.assetUrl ? 'Arte visual sintetizada no R2' : 'Arte pendente de renderização visual'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleSynthesizeImage(currentCreative.id, selectedCreativeIndex)}
                        disabled={synthesizingImageId === currentCreative.id}
                        className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-indigo-300 bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 flex items-center gap-1.5 transition-all disabled:opacity-50"
                      >
                        {synthesizingImageId === currentCreative.id ? (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            <span>Renderizando Arte...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>{currentCreative.assetUrl ? 'Regenerar Imagem' : 'Gerar Arte Visual com IA'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Coluna de Mockup Instagram Feed */}
                  <div className="lg:col-span-5 flex justify-center">
                    <div className="w-full max-w-[340px] rounded-2xl bg-slate-950 border border-slate-800 shadow-2xl overflow-hidden text-slate-100">
                      {/* Instagram Header */}
                      <div className="p-3 border-b border-slate-800/80 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-indigo-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs overflow-hidden border border-slate-700 flex-shrink-0">
                            {(activeTenantLogo || user?.logo_url) ? (
                              <img
                                src={activeTenantLogo || user?.logo_url || ''}
                                alt={currentProduct?.name || name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              (currentProduct?.name || name || 'P').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div>
                            <span className="text-xs font-bold block leading-tight">{currentProduct?.name || name}</span>
                            <span className="text-[10px] text-slate-400 block leading-tight">Patrocinado</span>
                          </div>
                        </div>
                        <Globe className="w-3.5 h-3.5 text-slate-500" />
                      </div>

                      {/* Creative Visual Canvas */}
                      <div className="relative aspect-square bg-slate-900 flex items-center justify-center overflow-hidden">
                        {currentCreative.assetUrl ? (
                          <img
                            src={currentCreative.assetUrl}
                            alt={currentCreative.headline}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center p-6 space-y-2">
                            <ImageIcon className="w-10 h-10 text-slate-700 mx-auto" />
                            <p className="text-xs text-slate-500">
                              Clique em "Gerar Arte Visual com IA" para sintetizar este criativo.
                            </p>
                          </div>
                        )}

                        {currentCreative.overlayText && (
                          <div className="absolute bottom-3 left-3 right-3 bg-black/75 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10 text-center shadow-lg">
                            <span className="text-[11px] font-black tracking-wider uppercase text-white drop-shadow">
                              {currentCreative.overlayText}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Instagram Footer & CTA */}
                      <div className="p-3 bg-slate-950/80 border-t border-slate-800/80 flex items-center justify-between">
                        <div className="max-w-[65%]">
                          <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider block truncate">
                            {landingUrl ? new URL(landingUrl.startsWith('http') ? landingUrl : `https://${landingUrl}`).hostname : 'seusite.com.br'}
                          </span>
                          <span className="text-xs font-bold text-white truncate block">
                            {currentCreative.headline}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-semibold text-[11px] shadow hover:bg-indigo-500 transition-colors uppercase tracking-wider"
                        >
                          {currentCreative.callToActionType?.replace('_', ' ') || 'Saiba Mais'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Botões de Ação Final */}
              <div className="flex justify-between items-center pt-4 border-t border-slate-800 flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 text-sm font-medium transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Voltar ao Parecer CGO
                </button>

                <div className="flex items-center gap-2.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      if (onSuccess) onSuccess(mcaResult.campaignId);
                      onClose();
                    }}
                    className="px-4 py-2 rounded-xl border border-slate-700 bg-slate-800/80 hover:bg-slate-800 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
                    title="Salva os 5 ângulos gerados e fecha o assistente sem disparar notificações"
                  >
                    <FileText className="w-3.5 h-3.5 text-slate-400" />
                    Salvar Rascunho
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await campaignsApi.transition(mcaResult.campaignId, 'em_revisao').catch(() => {});
                        await campaignsApi.notifyAdminWhatsApp(mcaResult.campaignId);
                        alert('✅ Solicitação de aprovação enviada com sucesso no WhatsApp do Gestor!');
                      } catch (err: any) {
                        console.warn('WhatsApp alert error:', err);
                      }
                      if (onSuccess) onSuccess(mcaResult.campaignId);
                      onClose();
                    }}
                    className="px-4 py-2 rounded-xl border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                    title="Move a campanha para Em Revisão e envia os criativos no WhatsApp do Gestor"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Solicitar Aprovação (WhatsApp)
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (onSuccess) onSuccess(mcaResult.campaignId);
                      onClose();
                    }}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold shadow-lg shadow-emerald-500/25 flex items-center gap-2 transition-all cursor-pointer"
                    title="Abre o Cockpit completo da Campanha (Estúdio Criativo, Canais e Telemetria)"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Salvar & Abrir Cockpit
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
